/**
 * InvoiceRecipientsList
 * Self-contained panel for managing invoice recipient email addresses for a client.
 * Usable from both SystemAdmin (ClientEdit.tsx) and ClientAdmin (Management.tsx) views.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, X, Plus, AlertCircle, Mail } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  getInvoiceRecipients,
  addInvoiceRecipient,
  removeInvoiceRecipient,
  InvoiceRecipient,
} from '../../services/invoiceRecipientsService';

interface Props {
  clientId: string;
}

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const InvoiceRecipientsList: React.FC<Props> = ({ clientId }) => {
  const [recipients, setRecipients] = useState<InvoiceRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getInvoiceRecipients(clientId);
      setRecipients(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) load();
  }, [clientId, load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = addEmail.trim();
    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (recipients.some((r) => r.email.toLowerCase() === trimmed.toLowerCase())) {
      setError('That email address is already in the list.');
      return;
    }

    try {
      setAdding(true);
      setError('');
      const newRecipient = await addInvoiceRecipient(clientId, trimmed);
      setRecipients((prev) => [...prev, newRecipient]);
      setAddEmail('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (recipientId: string) => {
    try {
      setRemovingId(recipientId);
      setError('');
      await removeInvoiceRecipient(clientId, recipientId);
      setRecipients((prev) => prev.filter((r) => r.id !== recipientId));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#64748b] text-sm py-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#c41e3a]" />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" />
          <span>{error}</span>
        </div>
      )}

      {/* Recipient list */}
      {recipients.length === 0 ? (
        <p className="text-sm text-[#94a3b8] italic">No recipients configured.</p>
      ) : (
        <ul className="space-y-2">
          {recipients.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-4 h-4 flex-shrink-0 text-[#94a3b8]" />
                <span className="text-[#0f172a] truncate">{r.email}</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(r.id)}
                disabled={removingId === r.id}
                className="flex-shrink-0 p-1 rounded hover:bg-red-50 text-[#94a3b8] hover:text-[#c41e3a] transition-colors disabled:opacity-50"
                title="Remove recipient"
              >
                {removingId === r.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add form */}
      <form onSubmit={handleAdd} className="flex items-center gap-2">
        <input
          type="email"
          value={addEmail}
          onChange={(e) => setAddEmail(e.target.value)}
          placeholder="name@example.com"
          disabled={adding}
          className="flex-1 px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={adding || !addEmail.trim()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-all shadow-sm shadow-[#c41e3a]/20 whitespace-nowrap"
        >
          {adding ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          Add
        </button>
      </form>
    </div>
  );
};

export default InvoiceRecipientsList;
